import { CaretDown, CircleNotch } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import {
  type ButtonHTMLAttributes,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
} from "react";

import { getWhitelabelCustomization } from "common/helpers/utils";

const selectButtonVariants = cva(
  [
    "group flex shrink-0 items-center rounded-2xl border border-black-100 bg-black-100 transition-[background,border] text-black-500",
    //hover
    "hover:bg-black-200 hover:border-black-200",
    //focus
    "focus-visible:bg-white focus-visible:outline-none",
    //disabled
    "disabled:cursor-not-allowed disabled:text-black-400 disabled:hover:bg-black-100 disabled:hover:border-black-100",
    //open
    "data-[state=open]:bg-white",
    //has selection
    "data-[selection=true]:text-black-950",
    //error
    "data-[error=true]:!border-red-400",
    //form field error
    "group-data-[error=true]:!border-red-400",
    //error + open
    "data-[error=true]:data-[state=open]:outline-red-300 data-[error=true]:data-[state=open]:outline-solid data-[error=true]:data-[state=open]:outline-2",
    //form field error + open
    "group-data-[error=true]:data-[state=open]:outline-red-300 group-data-[error=true]:data-[state=open]:outline-solid group-data-[error=true]:data-[state=open]:outline-2",
    //error + focus
    "data-[error=true]:focus-visible:outline-red-300 data-[error=true]:focus-visible:outline-solid data-[error=true]:focus-visible:outline-2",
    //form field error + focus
    "group-data-[error=true]:focus-visible:outline-red-300 group-data-[error=true]:focus-visible:outline-solid group-data-[error=true]:focus-visible:outline-2",
  ],
  {
    variants: {
      size: {
        sm: "h-8 gap-1 px-2 text-button-xs [&_svg]:size-4",
        md: "h-10 gap-1.5 px-2.5 text-button-sm [&_svg]:size-5",
        lg: "h-11 gap-2 px-3 text-button-md [&_svg]:size-5",
      },
      hasWhitelabelColor: {
        true: [
          //focus
          "focus-visible:border-whitelabel-primary focus-visible:hover:border-whitelabel-primary",
          //open
          "data-[state=open]:border-whitelabel-primary",
        ],
        false: [
          //focus
          "focus-visible:border-purple-400 focus-visible:hover:border-purple-400",
          //open
          "data-[state=open]:border-purple-400",
        ],
      },
    },
  },
);
const gaps = {
  sm: "gap-1",
  md: "gap-1.5",
  lg: "gap-2",
} as const;

interface SelectButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type">,
    VariantProps<typeof selectButtonVariants> {
  hasSelection: boolean;
  hasError?: boolean;
  rightIcon?: ReactNode;
  isLoading?: boolean;
  disabled?: boolean;
}
function SelectButton(
  {
    children,
    className,
    size = "md",
    hasError = false,
    hasSelection,
    rightIcon = null,
    isLoading = false,
    disabled = false,
    ...rest
  }: SelectButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <button
      {...rest}
      type="button"
      ref={ref}
      className={selectButtonVariants({
        size,
        className,
        hasWhitelabelColor: !!getWhitelabelCustomization()?.primary_color,
      })}
      data-error={!disabled && hasError}
      data-selection={!disabled && hasSelection}
      disabled={disabled}
    >
      <span className="flex-1 truncate text-left">{children}</span>
      <div className={clsx("flex items-center", gaps[size])}>
        {isLoading ? (
          <CircleNotch className="animate-spin fill-black-400" />
        ) : (
          rightIcon
        )}
        <CaretDown className="ml-auto text-black-950 transition-transform group-data-[state=open]:rotate-180" />
      </div>
    </button>
  );
}

const SelectButtonWithRef = forwardRef(SelectButton);
SelectButtonWithRef.displayName = "SelectButton";

export default SelectButtonWithRef;
